import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { View, Pressable } from "react-native-web";
import CustomText from "../atoms/CustomText";
import { colors, spacing, typography } from "../../utils/styling";
import { useCurrentProfile, useMyAdvisor } from "../../lib/profiles";
import { clearMoneyPersonalityAssessmentState } from "../../utils/quizRetake";
import { PERSONALITY_COLORS, toSlug } from "./personalityMeta";

// McKayla 2026-10-12: the same seven buttons on all five personality pages (and the
// dashboard), content tailored to the personality where it applies. Order and labels
// are hers. "Financial Tools" is a future addition she asked us to leave room for.

type ActionKey =
  | "download"
  | "exercises"
  | "resources"
  | "explore"
  | "advisor"
  | "retake"
  | "tools";

const ACTIONS: Array<{ key: ActionKey; label: string; desc: string }> = [
  {
    key: "download",
    label: "Download My Personality Overview",
    desc: "Your personalized PDF: strengths, habits, challenges and tips.",
  },
  {
    key: "exercises",
    label: "Interactive Exercises",
    desc: "Guided activities made for your personality.",
  },
  {
    key: "resources",
    label: "Financial Resources & Tips",
    desc: "Tips for putting what you learned into practice.",
  },
  {
    key: "explore",
    label: "Explore Other Personalities",
    desc: "See all five money personalities.",
  },
  {
    key: "advisor",
    label: "Connect With an Advisor",
    desc: "Talk your results through with a financial advisor.",
  },
  {
    key: "retake",
    label: "Retake the Quiz",
    desc: "Take the assessment again.",
  },
  {
    key: "tools",
    label: "Financial Tools",
    desc: "Budgeting, savings and goal tools. Coming soon.",
  },
];

export default function PersonalitySidePanel({
  personality,
  layout,
  onDownload,
  onExercises,
}: {
  personality: string;
  // "column": the right-hand panel on a personality page. "grid": tiles on the dashboard.
  layout: "column" | "grid";
  // Dashboard only: download this person's own results PDF / switch to the Exercises
  // tab in place. On the public pages both go to the dashboard (login if needed).
  onDownload?: () => Promise<void>;
  onExercises?: () => void;
}) {
  const navigate = useNavigate();
  const color = PERSONALITY_COLORS[personality] || colors.primary;
  const { data: profile } = useCurrentProfile();
  const isLoggedIn = !!profile;

  const [open, setOpen] = useState<ActionKey | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const { data: advisor, isLoading: advisorLoading } = useMyAdvisor(
    isLoggedIn && open === "advisor"
  );

  const handlePress = async (key: ActionKey) => {
    switch (key) {
      case "download":
        if (!onDownload) {
          navigate("/client");
          return;
        }
        setDownloading(true);
        setDownloadFailed(false);
        try {
          await onDownload();
        } catch {
          setDownloadFailed(true);
        } finally {
          setDownloading(false);
        }
        return;
      case "exercises":
        if (onExercises) onExercises();
        else navigate("/client?tab=exercises");
        return;
      case "resources":
        navigate(`/personalities/${toSlug(personality)}/resources`);
        return;
      case "explore":
        navigate("/personalities");
        return;
      case "advisor":
      case "retake":
        setOpen(open === key ? null : key);
        return;
      case "tools":
        return;
    }
  };

  const startRetake = () => {
    clearMoneyPersonalityAssessmentState();
    navigate("/quiz", { state: { from: window.location.pathname, retake: true } });
  };

  const isGrid = layout === "grid";

  return (
    <View
      style={{
        width: "100%",
        display: "flex",
        flexDirection: isGrid ? "row" : "column",
        flexWrap: isGrid ? "wrap" : "nowrap",
        gap: spacing.sm,
      }}
    >
      {ACTIONS.map(({ key, label, desc }) => {
        const disabled = key === "tools";
        const isOpen = open === key;
        return (
          <View
            key={key}
            style={{
              width: isGrid ? undefined : "100%",
              flexBasis: isGrid ? 260 : undefined,
              flexGrow: isGrid ? 1 : undefined,
            }}
          >
            <Pressable
              onPress={() => handlePress(key)}
              disabled={disabled || (key === "download" && downloading)}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: disabled
                  ? colors.gray[100]
                  : hovered || isOpen
                  ? `${color}26`
                  : `${color}14`,
                border: `2px solid ${disabled ? colors.gray[300] : `${color}55`}`,
                borderRadius: 14,
                padding: spacing.md,
                cursor: disabled ? "default" : "pointer",
                transition: "all 0.2s ease",
                opacity: disabled ? 0.75 : 1,
              })}
            >
              <CustomText
                style={{
                  fontSize: 16,
                  fontWeight: "700",
                  color: disabled ? colors.gray[600] : colors.dark,
                  fontFamily: typography.fontFamily.body,
                  marginBottom: 2,
                }}
              >
                {key === "download" && downloading ? "Preparing your PDF..." : label}
              </CustomText>
              <CustomText
                style={{
                  fontSize: 13,
                  color: colors.gray[600],
                  fontFamily: typography.fontFamily.body,
                  lineHeight: 18,
                }}
              >
                {key === "retake" && isLoggedIn
                  ? "Take it again. Your current results stay saved."
                  : desc}
              </CustomText>
            </Pressable>

            {key === "download" && downloadFailed && (
              <CustomText style={detailText}>
                The download didn't start. Please try again.
              </CustomText>
            )}

            {key === "advisor" && isOpen && (
              <View style={detailBox}>
                {!isLoggedIn ? (
                  <CustomText style={detailText}>
                    Log in to see the advisor connected to your results.
                  </CustomText>
                ) : advisorLoading ? (
                  <CustomText style={detailText}>Loading...</CustomText>
                ) : advisor ? (
                  <>
                    <CustomText style={{ ...detailText, color: colors.dark }}>
                      Your advisor{advisor.name ? `: ${advisor.name}` : ""}
                      {advisor.firm_name ? `, ${advisor.firm_name}` : ""}
                    </CustomText>
                    {advisor.email && (
                      <a
                        href={`mailto:${advisor.email}?subject=${encodeURIComponent(
                          "My Money Personality results"
                        )}`}
                        style={{
                          display: "inline-block",
                          marginTop: spacing.sm,
                          backgroundColor: color,
                          color: colors.white,
                          padding: "8px 16px",
                          borderRadius: 999,
                          textDecoration: "none",
                          fontFamily: "Nunito",
                          fontWeight: 700,
                          fontSize: 14,
                        }}
                      >
                        Email {advisor.name ? advisor.name.split(" ")[0] : "your advisor"}
                      </a>
                    )}
                  </>
                ) : (
                  <CustomText style={detailText}>
                    Booking a conversation with an advisor is coming soon.
                  </CustomText>
                )}
              </View>
            )}

            {key === "retake" && isOpen && (
              <View style={detailBox}>
                <CustomText style={{ ...detailText, color: colors.dark }}>
                  {isLoggedIn
                    ? "Start the quiz again? Your current results stay saved in your dashboard."
                    : "Start the quiz again?"}
                </CustomText>
                <View style={{ flexDirection: "row", gap: spacing.sm, marginTop: spacing.sm }}>
                  <Pressable
                    onPress={startRetake}
                    style={{
                      backgroundColor: color,
                      paddingVertical: 8,
                      paddingHorizontal: 16,
                      borderRadius: 999,
                      cursor: "pointer",
                    }}
                  >
                    <CustomText style={{ color: colors.white, fontWeight: "700", fontSize: 14, fontFamily: "Nunito" }}>
                      Yes, retake it
                    </CustomText>
                  </Pressable>
                  <Pressable
                    onPress={() => setOpen(null)}
                    style={{
                      backgroundColor: colors.white,
                      border: `1px solid ${colors.gray[300]}`,
                      paddingVertical: 8,
                      paddingHorizontal: 16,
                      borderRadius: 999,
                      cursor: "pointer",
                    }}
                  >
                    <CustomText style={{ color: colors.dark, fontWeight: "600", fontSize: 14, fontFamily: "Nunito" }}>
                      Cancel
                    </CustomText>
                  </Pressable>
                </View>
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
}

const detailBox = {
  backgroundColor: colors.white,
  border: `1px solid ${colors.gray[200]}`,
  borderRadius: 12,
  padding: spacing.md,
  marginTop: 6,
};

const detailText = {
  fontSize: 14,
  color: colors.gray[700],
  fontFamily: "Nunito",
  lineHeight: 20,
  marginTop: 4,
};
